import { useEffect, useMemo, useRef, useState } from 'react';
import CourseCover from './CourseCover';
import { HEAT, heatForStart } from '../lib/course-heat';
import { targetLanguageName } from '../data/target-languages';

export interface PreviewPair {
  qe: string;
  qs: string;
  v: string;
  ae: string;
  as: string;
}

type PreviewPattern = PreviewPair[];

export interface CoursePreview {
  key: string;
  title: string;
  cat: string;
  start: string;
  source: string;
  target: string;
  country: string;
  levels: Record<string, PreviewPattern[]>;
}

// Lazy-loaded per-course preview content (~1.5MB each, ~4000 pairs). Vite's import.meta.glob
// (non-eager) keeps every entry in its own chunk, so opening one course never pulls the other
// four preview files into the main bundle — only the one the user clicks is fetched.
const previewLoaders = import.meta.glob<CoursePreview>('../data/course-previews/*.json', {
  import: 'default',
});

function findPreviewLoader(key: string): (() => Promise<CoursePreview>) | undefined {
  return previewLoaders[`../data/course-previews/${key}.json`];
}

// The draft deck (course-previews) only ever covers the industrial-course CEFR span B1-C2 (the
// 5 built courses all start at B1). Transcripts mirror the same 4 levels x 3 days.
const DRAFT_LEVELS = ['B1', 'B2', 'C1', 'C2'] as const;
type DayNum = 1 | 2 | 3;
const TRANSCRIPT_DAYS: DayNum[] = [1, 2, 3];

type Section = 'draft' | 'transcripts';

// Draft patterns are rendered PATTERN_PAGE_SIZE at a time. Transcript patterns are an order of
// magnitude bigger (a single drilled pattern can run 1000+ lines), so they page much smaller.
const PATTERN_PAGE_SIZE = 8;
const TRANSCRIPT_PAGE_SIZE = 2;

interface TranscriptPattern {
  p: number;
  l: [string, string][];
}

interface TranscriptDoc {
  level: string;
  day: number;
  patterns: TranscriptPattern[];
}

type TranscriptCacheEntry =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ready'; data: TranscriptDoc };

export interface CourseDetailViewProps {
  courseKey: string;
  catalogTitle: string;
  onClose: () => void;
}

export default function CourseDetailView({ courseKey, catalogTitle, onClose }: CourseDetailViewProps) {
  const [preview, setPreview] = useState<CoursePreview | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [section, setSection] = useState<Section>('draft');
  const [draftLevel, setDraftLevel] = useState<string>(DRAFT_LEVELS[0]);
  const [transcriptLevel, setTranscriptLevel] = useState<string>(DRAFT_LEVELS[0]);
  const [transcriptDay, setTranscriptDay] = useState<DayNum>(1);

  const [transcriptCache, setTranscriptCache] = useState<Record<string, TranscriptCacheEntry>>({});

  const [visibleCount, setVisibleCount] = useState(PATTERN_PAGE_SIZE);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  // Load the draft preview once, when the course changes.
  useEffect(() => {
    let cancelled = false;
    setPreview(null);
    setError(null);
    setSection('draft');
    setDraftLevel(DRAFT_LEVELS[0]);
    setTranscriptLevel(DRAFT_LEVELS[0]);
    setTranscriptDay(1);
    setTranscriptCache({});

    const loader = findPreviewLoader(courseKey);
    if (!loader) {
      setError('No preview available for this course yet.');
      return;
    }
    loader()
      .then((data) => {
        if (cancelled) return;
        setPreview(data);
        const levels = DRAFT_LEVELS.filter((l) => l in data.levels);
        setDraftLevel(levels[0] ?? Object.keys(data.levels)[0] ?? DRAFT_LEVELS[0]);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : 'Failed to load course preview.');
      });
    return () => {
      cancelled = true;
    };
  }, [courseKey]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Fetch the active transcript tab lazily, only once it's opened, and only if not already cached.
  useEffect(() => {
    if (section !== 'transcripts') return;
    const cacheKey = `${transcriptLevel}_d${transcriptDay}`;
    setTranscriptCache((prev) => {
      if (prev[cacheKey]) return prev;
      return { ...prev, [cacheKey]: { status: 'loading' } };
    });
  }, [section, transcriptLevel, transcriptDay]);

  useEffect(() => {
    if (section !== 'transcripts') return;
    const cacheKey = `${transcriptLevel}_d${transcriptDay}`;
    const entry = transcriptCache[cacheKey];
    if (!entry || entry.status !== 'loading') return;

    let cancelled = false;
    const url = `${import.meta.env.BASE_URL}course-transcripts/${courseKey}/${transcriptLevel}_d${transcriptDay}.json`;
    fetch(url)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json() as Promise<TranscriptDoc>;
      })
      .then((data) => {
        if (cancelled) return;
        setTranscriptCache((prev) => ({ ...prev, [cacheKey]: { status: 'ready', data } }));
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setTranscriptCache((prev) => ({
          ...prev,
          [cacheKey]: { status: 'error', message: e instanceof Error ? e.message : 'Failed to load transcript.' },
        }));
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally excludes transcriptCache to avoid refetch loop
  }, [section, transcriptLevel, transcriptDay, courseKey]);

  // Reset the lazy-render window whenever the active tab changes, so switching tabs doesn't
  // carry over a huge already-rendered pattern list.
  useEffect(() => {
    setVisibleCount(section === 'draft' ? PATTERN_PAGE_SIZE : TRANSCRIPT_PAGE_SIZE);
  }, [section, draftLevel, transcriptLevel, transcriptDay]);

  const levels = useMemo(() => {
    if (!preview) return [];
    const keys = Object.keys(preview.levels);
    const ordered: string[] = DRAFT_LEVELS.filter((l) => keys.includes(l));
    const extra = keys.filter((k) => !ordered.includes(k));
    return [...ordered, ...extra];
  }, [preview]);

  const draftPatterns: PreviewPattern[] = draftLevel && preview ? preview.levels[draftLevel] ?? [] : [];
  const transcriptCacheKey = `${transcriptLevel}_d${transcriptDay}`;
  const transcriptEntry = transcriptCache[transcriptCacheKey];
  const transcriptPatterns: TranscriptPattern[] =
    transcriptEntry?.status === 'ready' ? transcriptEntry.data.patterns : [];

  const activeTotal = section === 'draft' ? draftPatterns.length : transcriptPatterns.length;

  // Lazy-render patterns: reveal more as the user scrolls near the bottom instead of
  // rendering everything at once or pulling in a separate virtualization dependency.
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const step = section === 'draft' ? PATTERN_PAGE_SIZE : TRANSCRIPT_PAGE_SIZE;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setVisibleCount((v) => Math.min(v + step, activeTotal));
        }
      },
      { rootMargin: '600px' },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [section, activeTotal]);

  const heatKey = preview ? heatForStart(preview.start) : 'hot';
  const heat = HEAT[heatKey];
  const isCoverTab = section === 'draft' && levels.length > 0 && draftLevel === levels[0];
  const targetLang = preview ? targetLanguageName(preview.target.split('_')[0] ?? preview.target) : '';
  const visibleDraftPatterns = draftPatterns.slice(0, visibleCount);
  const visibleTranscriptPatterns = transcriptPatterns.slice(0, visibleCount);

  return (
    <div className="fixed inset-0 z-50 flex md:items-center md:justify-center bg-black/70 md:p-6" onClick={onClose}>
      <div
        className="relative flex flex-col w-full h-full md:h-auto md:max-h-[90vh] md:max-w-4xl md:rounded-xl overflow-hidden border border-white/10 font-body"
        style={{ background: heat.bg }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header / back button */}
        <div className="shrink-0 flex items-center gap-3 px-4 md:px-6 py-3 border-b border-white/10 bg-black/20">
          <button
            onClick={onClose}
            className="font-body text-sm text-gray-200 hover:text-white flex items-center gap-1 shrink-0"
          >
            <span aria-hidden>←</span> Back
          </button>
          <h2 className="font-heading text-lg md:text-xl font-semibold truncate" style={{ color: '#f3efe6' }}>
            {preview?.title ?? catalogTitle}
          </h2>
        </div>

        {error && (
          <div className="flex-1 flex items-center justify-center p-8">
            <p className="font-body text-sm text-gray-300">{error}</p>
          </div>
        )}

        {!error && !preview && (
          <div className="flex-1 flex items-center justify-center p-8">
            <p className="font-mono text-xs text-gray-400">Loading course preview…</p>
          </div>
        )}

        {preview && (
          <>
            {/* Section row: Draft (deck) vs Transcripts (audio) */}
            <div className="shrink-0 px-4 md:px-6 pt-3 overflow-x-auto">
              <div className="flex items-center gap-2 whitespace-nowrap">
                <button
                  onClick={() => setSection('draft')}
                  className="px-3 py-1 rounded-full text-xs font-body font-semibold border transition-colors"
                  style={
                    section === 'draft'
                      ? { borderColor: heat.accent, color: heat.badgeText, background: heat.accent }
                      : { borderColor: 'rgba(255,255,255,0.15)', color: '#b9b2a4' }
                  }
                >
                  Draft (deck)
                </button>
                <button
                  onClick={() => setSection('transcripts')}
                  className="px-3 py-1 rounded-full text-xs font-body font-semibold border transition-colors"
                  style={
                    section === 'transcripts'
                      ? { borderColor: heat.accent, color: heat.badgeText, background: heat.accent }
                      : { borderColor: 'rgba(255,255,255,0.15)', color: '#b9b2a4' }
                  }
                >
                  Transcripts (audio)
                </button>
              </div>
            </div>

            {/* Draft level tabs: B1 B2 C1 C2 */}
            {section === 'draft' && (
              <div className="shrink-0 px-4 md:px-6 pt-2 pb-3 border-b border-white/10 overflow-x-auto">
                <div className="flex items-center gap-2 whitespace-nowrap">
                  {levels.map((l) => {
                    const active = draftLevel === l;
                    return (
                      <button
                        key={l}
                        onClick={() => setDraftLevel(l)}
                        className="px-3 py-1 rounded-full text-xs font-mono border transition-colors"
                        style={
                          active
                            ? { borderColor: heat.accent, color: heat.accent, background: `${heat.accent}22` }
                            : { borderColor: 'rgba(255,255,255,0.15)', color: '#b9b2a4' }
                        }
                      >
                        {l}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Transcript tabs: Level row + Day row (4 x 3 = 12 combinations) */}
            {section === 'transcripts' && (
              <div className="shrink-0 px-4 md:px-6 pt-2 pb-3 border-b border-white/10 space-y-2">
                <div className="overflow-x-auto">
                  <div className="flex items-center gap-2 whitespace-nowrap">
                    {DRAFT_LEVELS.map((l) => {
                      const active = transcriptLevel === l;
                      return (
                        <button
                          key={l}
                          onClick={() => setTranscriptLevel(l)}
                          className="px-3 py-1 rounded-full text-xs font-mono border transition-colors"
                          style={
                            active
                              ? { borderColor: heat.accent, color: heat.accent, background: `${heat.accent}22` }
                              : { borderColor: 'rgba(255,255,255,0.15)', color: '#b9b2a4' }
                          }
                        >
                          {l}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <div className="flex items-center gap-2 whitespace-nowrap">
                    {TRANSCRIPT_DAYS.map((d) => {
                      const active = transcriptDay === d;
                      return (
                        <button
                          key={d}
                          onClick={() => setTranscriptDay(d)}
                          className="px-3 py-1 rounded text-xs font-body font-medium border transition-colors"
                          style={
                            active
                              ? { borderColor: heat.accent, color: heat.badgeText, background: heat.accent }
                              : { borderColor: 'rgba(255,255,255,0.15)', color: '#b9b2a4' }
                          }
                        >
                          Day {d}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* Content */}
            <div className="flex-1 overflow-y-auto px-4 md:px-6 py-4 space-y-8">
              {isCoverTab && (
                <div className="flex flex-wrap items-start gap-5 pb-5 border-b border-white/10">
                  <CourseCover
                    category={preview.cat}
                    targetLanguageName={targetLang}
                    courseTitle={preview.title}
                    heat={heatKey}
                    className="w-[200px] md:w-[220px]"
                  />
                </div>
              )}

              {section === 'draft' &&
                visibleDraftPatterns.map((pattern, pIdx) => (
                  <div key={pIdx} className="space-y-4">
                    <h4 className="font-heading text-base font-semibold" style={{ color: heat.accent }}>
                      Pattern {pIdx + 1}
                    </h4>
                    <div className="space-y-3">
                      {pattern.map((pair, qIdx) => (
                        <div key={qIdx} className="space-y-1">
                          <div className="flex items-baseline gap-2">
                            <span className="font-mono text-[10px] text-gray-500 shrink-0 w-3">Q</span>
                            <div className="min-w-0">
                              <p className="text-sm" style={{ color: '#e8e3d8' }}>
                                {pair.qe}
                              </p>
                              <p className="text-sm" style={{ color: heat.accent }}>
                                {pair.qs}
                              </p>
                              <p className="font-mono text-[11px] text-gray-500 mt-0.5">{pair.v}</p>
                            </div>
                          </div>
                          <div className="flex items-baseline gap-2 pl-6">
                            <span className="font-mono text-[10px] text-gray-500 shrink-0 w-3">A</span>
                            <div className="min-w-0">
                              <p className="text-sm" style={{ color: '#e8e3d8' }}>
                                {pair.ae}
                              </p>
                              <p className="text-sm" style={{ color: heat.accent }}>
                                {pair.as}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}

              {section === 'transcripts' && (!transcriptEntry || transcriptEntry.status === 'loading') && (
                <p className="font-mono text-xs text-gray-400">Loading transcript…</p>
              )}
              {section === 'transcripts' && transcriptEntry?.status === 'error' && (
                <p className="font-body text-sm text-gray-300">{transcriptEntry.message}</p>
              )}

              {section === 'transcripts' &&
                visibleTranscriptPatterns.map((pattern) => (
                  <div key={pattern.p} className="space-y-3">
                    <h4 className="font-heading text-base font-semibold" style={{ color: heat.accent }}>
                      Pattern {pattern.p}
                    </h4>
                    <div className="space-y-1.5">
                      {pattern.l.map(([role, text], lIdx) => (
                        <div key={lIdx} className="flex items-baseline gap-2">
                          <span className="font-mono text-[10px] text-gray-500 shrink-0 w-3">{role}</span>
                          {role === 'C' ? (
                            <p className="font-mono text-xs text-gray-500 min-w-0">{text}</p>
                          ) : (
                            <p className="text-sm min-w-0" style={{ color: heat.accent }}>
                              {text}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}

              {visibleCount < activeTotal && (
                <div ref={sentinelRef} className="py-4 text-center">
                  <button
                    onClick={() => {
                      const step = section === 'draft' ? PATTERN_PAGE_SIZE : TRANSCRIPT_PAGE_SIZE;
                      setVisibleCount((v) => Math.min(v + step, activeTotal));
                    }}
                    className="font-mono text-[11px] text-gray-400 hover:text-gray-200 underline underline-offset-2"
                  >
                    Load more patterns ({visibleCount}/{activeTotal})
                  </button>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
